"use client"

import { ampli } from "@/ampli"
import { SubscriptionCheckoutModal } from "@/components/Subscriptions/SubscriptionCheckoutModal"
import { UnlockJobFiltersModal } from "@/components/Subscriptions/UnlockJobFiltersModal"
import { graphql } from "@/gql"
import {
  CreativeJobsDocument,
  CreativeJobsQuery as GeneratedCreativeJobsQuery,
  JobPostingLocationType,
} from "@/gql/graphql"
import { useBreakpoint } from "@/hooks/useBreakpoint"
import { decodeGraphqlNodeId } from "@/tracking"
import { NovaSession } from "@/types"
import { useQuery } from "@apollo/client"
import { useSearchParams } from "next/navigation"
import { useEffect, useMemo, useState } from "react"
import { JobDetail } from "../components/JobDetail"
import { JobsFeed } from "../components/JobFeed/JobsFeed"
import { JobsBoardSideDrawer } from "../components/JobsBoardSideDrawer"
import { JobsHeader } from "../components/JobsHeader"
import { WhereDoYouWantToStartModal } from "@/components/WhereDoYouWantToStartModal"
import { useRouter } from "next-nprogress-bar"

graphql(`
  query CreativeJobs(
    $first: Int
    $after: String
    $position: [ID]
    $skills: [ID]
    $creativeWorkType: [ID]
    $workType: String
    $location: String
    $locationType: JobPostingLocationType
    $creativeId: ID!
  ) {
    individualCreative(id: $creativeId) {
      id
      __typename
      activeSubmittedJobApplicationCount
      activeReceivedJobInvitationCount

      proMembership {
        id
        activeMembershipType
      }

      ...JobsFeedIndividualCreative
    }
    allJobPostings(
      first: $first
      after: $after
      position: $position
      skills: $skills
      creativeWorkType: $creativeWorkType
      status: PUBLISHED
      workType: $workType
      location: $location
      locationType: $locationType
      acceptingApplications: true
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          __typename
          ...JobsBoardSideDrawer
          ...JobDetailJobPosting
        }
      }
      ...JobsFeedJobPostingConnection
    }
  }
`)

interface SearchParams {
  [key: string]: string
}

export type JobBoardSideDrawerData = {
  jobPostingId: string | null
  jobPostingType: "creative" | "client" | null
  positionId: string | null
}

interface Props {
  serverSession: NovaSession
  serverData: GeneratedCreativeJobsQuery
}

export const JobsClientPage: React.FC<Props> = ({ serverSession, serverData }) => {
  const [jobPostingIdForCheckoutModal, setJobPostingIdForCheckoutModal] = useState<string | null>(null)
  const [openCheckoutModal, setOpenCheckoutModal] = useState(false)
  const [showWhereToStartModal, setShowWhereToStartModal] = useState(false)
  const [openUnlockJobFiltersModal, setOpenUnlockJobFiltersModal] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const currentParams = useMemo(() => {
    const params: SearchParams = {}
    searchParams?.forEach((value, key) => {
      params[key] = value
    })
    return params
  }, [searchParams])
  const {
    data: clientData,
    fetchMore,
    networkStatus,
    loading,
  } = useQuery(CreativeJobsDocument, {
    fetchPolicy: "cache-and-network",
    variables: {
      creativeId: serverSession.user.creative.id!,
      first: 10,
      after: null,
      position: currentParams?.roles?.split(",") ?? null,
      skills: currentParams?.skills?.split(",") ?? null,
      creativeWorkType: currentParams?.creativeWorkType?.split(",") ?? null,
      workType:
        currentParams?.workType?.split(",")?.[0] === "all"
          ? null
          : currentParams?.workType?.split(",")?.[0]
            ? currentParams?.workType?.split(",")?.[0]
            : "paid",
      locationType: (currentParams?.locationType as JobPostingLocationType) ?? null,
      location: currentParams?.location ?? null,
    },
    context: {
      version: 2,
    },
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [drawer, setDrawer] = useState({
    hasImage: false,
    open: false,
  })
  const [jobPostingData, setJobPostingData] = useState<JobBoardSideDrawerData>({
    jobPostingId: null,
    jobPostingType: null,
    positionId: null,
  })

  useEffect(() => {
    if (currentParams.newCreative === "true") {
      setShowWhereToStartModal(true)
    }
  }, [currentParams, router])

  const data = clientData || serverData

  const onClickJobPosting = (
    jobPostingId: string,
    type: "client" | "creative",
    hasImage: boolean,
    positionId: string,
    index: number,
    redacted: boolean
  ) => {
    fetch("/api/view/job", {
      method: "POST",
      body: JSON.stringify({ id: jobPostingId }),
    })

    const { numericalId } = decodeGraphqlNodeId(jobPostingId)
    if (!numericalId) return
    ampli.jobCardClick({
      job_id: numericalId,
      redacted: redacted,
    })

    setSelectedIndex(index)
    setJobPostingData({
      jobPostingId,
      jobPostingType: type,
      positionId,
    })
    setDrawer({
      hasImage,
      open: true,
    })
  }

  const onOpenChange = (open: boolean) => {
    setDrawer({
      ...drawer,
      open,
    })
  }

  const openSubscriptionCheckoutModal = (entryPoint: "job-board" | "apply-to-job") => {
    setCheckoutModalEntryPoint(entryPoint)
    setOpenCheckoutModal(true)
  }

  const currentBreakpoint = useBreakpoint()
  const hideDrawer = currentBreakpoint && ["xl", "lg"].includes(currentBreakpoint)

  const creative = data?.individualCreative?.__typename === "IndividualCreative" ? data.individualCreative : null

  const selectedJob = data?.allJobPostings?.edges?.[selectedIndex]?.node

  const hasSubscription = creative?.proMembership?.activeMembershipType !== null

  const [checkoutModalEntryPoint, setCheckoutModalEntryPoint] = useState<
    "job-board-filters" | "job-board" | "apply-to-job" | "apply-to-jobs-shortcut-profile-setup"
  >("job-board")

  const onClickFilterWithoutSubscription = () => {
    ampli.upsellModalView({
      entry_point: "job-board-filter",
    })
    setOpenUnlockJobFiltersModal(true)
  }

  const onClickGoProFromUnlockFilterModal = () => {
    ampli.upsellModalClickThrough({
      entry_point: "job-board-filter",
    })
    setCheckoutModalEntryPoint("job-board-filters")
    setOpenUnlockJobFiltersModal(false)
    setOpenCheckoutModal(true)
  }

  const handleUnlockJobFiltersModalOpenChange = (open: boolean) => {
    if (!open) {
      ampli.upsellModalExit({
        entry_point: "job-board-filter",
      })
    }
    setOpenUnlockJobFiltersModal(open)
  }

  return (
    <div className="flex w-full flex-col">
      <WhereDoYouWantToStartModal
        onClose={() => {
          setShowWhereToStartModal(false)
        }}
        isOpen={showWhereToStartModal}
        onApplyToAJobClick={() => {
          // if (!hasSubscription) {
          setOpenCheckoutModal(true)
          setCheckoutModalEntryPoint("apply-to-jobs-shortcut-profile-setup")
          // }
          setShowWhereToStartModal(false)
        }}
        creativeHandle={serverSession?.user?.creative?.handle as string}
      />
      {/* Need to always render sFsubscription modal for success screen */}
      <SubscriptionCheckoutModal
        open={openCheckoutModal}
        onOpenChange={setOpenCheckoutModal}
        continueApplyingJobPostingId={jobPostingIdForCheckoutModal}
        entryPoint={checkoutModalEntryPoint}
      />
      {!hasSubscription && (
        <UnlockJobFiltersModal
          isOpen={openUnlockJobFiltersModal}
          onOpenChange={handleUnlockJobFiltersModalOpenChange}
          onClickGoPro={onClickGoProFromUnlockFilterModal}
        />
      )}
      {!hideDrawer && (
        <JobsBoardSideDrawer
          open={drawer.open}
          openSubscriptionCheckoutModal={(jobPostingId: string) => {
            setJobPostingIdForCheckoutModal(jobPostingId)
            openSubscriptionCheckoutModal("apply-to-job")
          }}
          jobPosting={selectedJob}
          jobPostingHasImage={drawer.hasImage}
          onOpenChange={onOpenChange}
          serverSession={serverSession}
          withdrawMutationRefetchQueries={[CreativeJobsDocument]}
        />
      )}
      <JobsHeader
        hasSubscription={hasSubscription}
        onClickFilterWithoutSubscription={onClickFilterWithoutSubscription}
        serverSession={serverSession}
        activeApplicationsCount={creative?.activeSubmittedJobApplicationCount}
        activeInvitationsCount={creative?.activeReceivedJobInvitationCount}
      />
      <div className="w-full bg-bg-2 lg:pl-12">
        <div className="relative mx-auto grid max-w-xl grid-cols-12 gap-8 sm:px-5 md:px-12 lg:px-0">
          <div className="col-span-12 lg:col-span-6">
            <div className="h-full w-full sm:pt-[40px]">
              <JobsFeed
                onClickJobPosting={onClickJobPosting}
                fetchMore={fetchMore}
                currentBreakpoint={currentBreakpoint}
                networkStatus={networkStatus}
                loadingJobPosts={loading}
                creative={creative}
                jobPostings={data?.allJobPostings}
                selectedJobPostingId={jobPostingData.jobPostingId}
                selectedIndex={selectedIndex}
                shouldHightlightSelectedIndex={Boolean(hideDrawer)}
                handleOpenCheckoutModal={() => openSubscriptionCheckoutModal("job-board")}
              />
            </div>
          </div>
          <div className="col-span-12 hidden lg:col-span-6 lg:block">
            <div className="sticky top-[144px] flex h-[calc(100dvh-145px)] w-full flex-col overflow-y-scroll pl-6 pr-12 pt-10">
              <JobDetail
                serverSession={serverSession}
                loading={!selectedJob}
                openSubscriptionCheckoutModal={(jobPostingId: string) => {
                  setJobPostingIdForCheckoutModal(jobPostingId)
                  openSubscriptionCheckoutModal("apply-to-job")
                }}
                jobPosting={selectedJob}
                withdrawMutationRefetchQueries={[CreativeJobsDocument]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
